import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import reconnecting from "../../../assets/topstudio/images/rolet/reconnecting.gif";
import React, { useEffect, useRef, useState } from "react";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TpStudioLoader from "../../client/tp-studio/tp-studio-loader";
import "../../../assets/topstudio/styles/Player.css";
import Volume from "./Volume";
import useSocket from "../../../hooks/useSocket";
import { useDispatch, useSelector } from "react-redux";

import { setUserData } from "../../../store/userSlice";
import LotteryRules from "./pop-up/lottery-rules";
import axiosInstance from "../../../utils/axiosInstance";
import { isMobile } from "react-device-detect";
import TeenPattisCasinoBetslipWeb from "./teenpatti_casino_betslip_web";
import TPPopUpBetSlip from "./teenpatti_casino_betslip_popup.jsx";
import LotteryResult from "./tp-result/LotteryResult.jsx";

const TP_RANKS = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K"
];
/** A–10 in pick row order (matches betting UI left → right). */
const LOTTERY_PICK_RANKS = TP_RANKS.slice(0, 10);

/** Decimal odds sent with place-bet (matches UI payout labels). */
function lotteryPayoutOdds(mode) {
  if (mode === "double") return 80;
  if (mode === "triple") return 700;
  return 9;
}

function normalizeLotteryResults(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    return Object.entries(raw)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([mid, cards]) => ({ cat_mid: mid, result: cards }));
  }
  return [];
}

/** Matches Teen Patti codes like 6SS, 10HH, ASS (rank + HH|SS|DD|CC). */
const LOTTERY_CARD_TOKEN_RE = /((?:10|[2-9]|[AJQK])(?:HH|SS|DD|CC))/gi;

function parseLotteryCardTokens(source) {
  const s = String(source ?? "").trim();
  if (!s) return [];
  const tokens = [];
  let m;
  const re = new RegExp(LOTTERY_CARD_TOKEN_RE.source, "gi");
  while ((m = re.exec(s)) !== null) tokens.push(m[1]);
  return tokens;
}

/** Lottery bet rank sent to API: Ace = 1, 2–10 as face value. */
function lotteryRankToBetDigit(rankStr) {
  if (rankStr === "A") return "1";
  return rankStr;
}

function lotteryPickIndexToBetDigit(rankIdx) {
  const rank = LOTTERY_PICK_RANKS[rankIdx];
  if (rank == null) return String(rankIdx);
  return lotteryRankToBetDigit(rank);
}

function lotteryPickCardCode(rankLabel) {
  // Pick-row PNGs: HH = spades in /assets/images/cards/
  return `${rankLabel}HH`;
}

function lotteryPickCardSrc(rankLabel) {
  return `/assets/images/cards/${lotteryPickCardCode(rankLabel)}.svg`;
}

function lotteryRankToDigitToken(rankStr) {
  return lotteryRankToBetDigit(rankStr);
}

/** Result line like 2HH-10DD-ACS → 2-0-1; footer + Results tab. */
function formatLotteryResultCompact(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const normalized = s.replace(/,/g, "-");
  const tokens = parseLotteryCardTokens(normalized);
  if (tokens.length === 0) {
    if (/^\d+(?:-\d+)+$/.test(normalized.replace(/\s/g, ""))) return normalized;
    return normalized;
  }
  return tokens
    .map((tok) => lotteryRankToDigitToken(tok.slice(0, -2)))
    .join("-");
}

function formatLotteryResultChip(row) {
  if (row == null) return "";
  if (typeof row === "string" || typeof row === "number") {
    return formatLotteryResultCompact(row);
  }
  if (row.result != null && row.result !== "") {
    return formatLotteryResultCompact(row.result);
  }
  const parts = [row.ac1, row.ac2, row.ac3, row.c1, row.c2, row.c3].filter(
    (x) => x !== undefined && x !== null && x !== ""
  );
  if (parts.length) return formatLotteryResultCompact(parts.join(","));
  return formatLotteryResultCompact(row.summary ?? row.display ?? "");
}

function displayLotteryRunner(rnr) {
  const s = String(rnr || "").trim();
  const modern = s.match(/^(single|double|triple)-(\d+)$/i);
  if (modern) {
    const label =
      modern[1].charAt(0).toUpperCase() + modern[1].slice(1).toLowerCase();
    return `${label}-${modern[2]}`;
  }
  const legacy = s.match(/^b(\d)_r(\d+)$/i);
  if (!legacy) return s || "";
  const labels = ["1st", "2nd", "3rd"];
  const idx = Number(legacy[1]) - 1;
  const rIdx = Number(legacy[2]);
  let rankLabel = legacy[2];
  if (TP_RANKS[rIdx] != null) {
    rankLabel = TP_RANKS[rIdx];
  }
  let rowWord = legacy[1];
  if (idx >= 0 && idx < labels.length) {
    rowWord = labels[idx];
  }
  return `${rowWord} card · ${rankLabel}`;
}

export default function Lottery() {
  const dispatch = useDispatch();

  const [showRules, setShowRules] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayLoader, setDisplayLoader] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const [roundId, setRoundId] = useState("");
  const [resultData, setResultData] = useState([]);
  const [showBetSlip, setShowBetSlip] = useState(false);
  const [showBetSlipPopup, setShowBetSlipPopup] = useState(false);
  const [placeBetObject, setPlaceBetObject] = useState({});
  const [betErrorMessageObj, setBetErrorMessage] = useState({});
  const [placedBets, setPlacedBets] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [betButtonShow] = useState(true);
  const [betButtonShowLoaderShow] = useState(false);
  const [isSuspended, setIsSuspended] = useState(true);
  const prevCatMidRef = useRef(null);
  const prevEvtStatusRef = useRef(null);
  const sessionUser = useSelector((s) => s.userStore.data);

  /* ================= SEQUENTIAL CARD REVEAL ================= */
  const flipTimerRef = useRef(null);
  const revealStartedForRef = useRef(null);
  const [shownAc1, setShownAc1] = useState(null);
  const [shownAc2, setShownAc2] = useState(null);
  const [shownAc3, setShownAc3] = useState(null);

  const { data } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "lottery_virtual"
  });
  //console.log("data:", data);

  const socketInner = data?.data;
  const payload = socketInner?.data;
  const payloadResult = socketInner?.result;

  const updateUserData = async (userData) => {
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };

  const fetchProfile = async () => {
    try {
      const { data } = await axiosInstance.get("/profile");
      setProfileData(data);
    } catch (e) {
      console.log("Failed to load profile data:", e);
    }
  };

  const getPlacedBets = async () => {
    const mid = payload?.cat_mid;
    if (mid == null || mid === "") return;
    try {
      const res = await axiosInstance.post("/casino/get-placed-bets", {
        cat_mid: mid,
        gametype: "lottery_virtual"
      });
      let bets = [];
      if (Array.isArray(res.data)) {
        bets = res.data;
      }
      setPlacedBets(bets);
    } catch (e) {
      setPlacedBets([]);
    }
  };

  const getBook = async () => {
    const mid = payload?.cat_mid;
    if (mid == null || mid === "") return;
    try {
      await axiosInstance.post("/casino/get-book", {
        cat_mid: mid,
        gametype: "lottery_virtual"
      });
    } catch (e) {
      /* optional book */
    }
  };

  const recentResultChips = Array.isArray(resultData)
    ? resultData
      .slice(0, 6)
      .map((row) => formatLotteryResultChip(row))
      .filter((s) => s !== "")
    : [];

  /* ================= TIMER ================= */
  const TOTAL_SECONDS = 30;
  const [timerTotal, setTimerTotal] = useState(TOTAL_SECONDS);
  const [timeLeft, setTimeLeft] = useState(TOTAL_SECONDS);
  const localTimerRef = useRef(null);

  useEffect(() => {
    if (!payload) return;

    updateUserData(data?.user);

    setDisplayLoader(false);
    setRoundId(payload?.cat_mid ?? "");

    if (
      prevEvtStatusRef.current !== payload.evt_status &&
      payload.evt_status === "OPEN"
    ) {
      setPlacedBets([]);
    }
    prevEvtStatusRef.current = payload.evt_status;

    const serverSeconds = Number(payload?.left_time);
    if (!Number.isNaN(serverSeconds)) {
      setTimerTotal((cap) => Math.max(cap, serverSeconds));
      setTimeLeft(Math.max(0, serverSeconds));
    }

    if (payload?.left_time >= 28 && payload?.left_time <= 30) {
      setIsSuspended(true);
      setShowBetSlipPopup(false);
    } else {
      setIsSuspended(false);
    }

    if (
      payload?.suspend1 === 1 ||
      payload?.suspend2 === 1 ||
      payload?.suspend3 === 1
    ) {
      setIsSuspended(true);
    }
    if (payload?.evt_status === "CLOSED") {
      setIsSuspended(true);
    }

    clearInterval(localTimerRef.current);
    localTimerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 0) {
          return prev - 1;
        }
        return 0;
      });
    }, 1000);
  }, [payload, data?.user, dispatch]);

  useEffect(() => {
    if (
      payloadResult === undefined ||
      payloadResult === null ||
      payloadResult === ""
    ) {
      return;
    }
    setResultData(normalizeLotteryResults(payloadResult));
  }, [payloadResult]);

  useEffect(() => () => clearInterval(localTimerRef.current), []);

  useEffect(() => {
    if (!payload?.evt_status) return;

    if (payload.evt_status === "OPEN") {
      clearTimeout(flipTimerRef.current);
      revealStartedForRef.current = null;
      setShownAc1(null);
      setShownAc2(null);
      setShownAc3(null);
      return;
    }

    const status = String(payload.evt_status).toUpperCase();
    if (status !== "CLOSED") return;

    const c1 = payload?.ac1;
    const c2 = payload?.ac2;
    const c3 = payload?.ac3;

    // Only valid card codes (e.g. "6SS", "10HH") trigger the reveal
    const isValidCard = (v) =>
      v && v !== "1" && parseLotteryCardTokens(String(v)).length > 0;

    if (!isValidCard(c1) && !isValidCard(c2) && !isValidCard(c3)) return;

    const roundKey = `${c1}|${c2}|${c3}`;
    if (revealStartedForRef.current === roundKey) return;
    revealStartedForRef.current = roundKey;

    clearTimeout(flipTimerRef.current);
    setShownAc1(null);
    setShownAc2(null);
    setShownAc3(null);

    // Build the reveal sequence for only valid cards
    const steps = [];
    if (isValidCard(c1)) steps.push(() => setShownAc1(c1));
    if (isValidCard(c2)) steps.push(() => setShownAc2(c2));
    if (isValidCard(c3)) steps.push(() => setShownAc3(c3));

    let pos = 0;
    let cancelled = false;

    function revealNext() {
      if (cancelled || pos >= steps.length) return;
      steps[pos]();
      pos++;
      if (pos < steps.length) {
        flipTimerRef.current = setTimeout(revealNext, 1000);
      }
    }

    flipTimerRef.current = setTimeout(revealNext, 300);

    return () => {
      cancelled = true;
      clearTimeout(flipTimerRef.current);
    };
  }, [payload?.evt_status, payload?.ac1, payload?.ac2, payload?.ac3]);

  useEffect(() => () => clearTimeout(flipTimerRef.current), []);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const mid = payload?.cat_mid;
    if (mid == null || mid === "") return;
    if (prevCatMidRef.current === mid) return;
    prevCatMidRef.current = mid;
    void getPlacedBets();
    void getBook();
  }, [payload?.cat_mid]);

  const lotteryRowBackPrice = (rowIndex) => {
    const v = Number(payload?.[`b${rowIndex + 1}`]);
    if (Number.isFinite(v) && v > 0) {
      return v;
    }
    return 0;
  };

  const openLotteryBetSlip = (mode, rankIndexesByRow) => {
    const digits = rankIndexesByRow.map(lotteryPickIndexToBetDigit);
    let teamKey;
    if (mode === "single") {
      teamKey = `Single-${digits[0]}`;
    } else if (mode === "double") {
      teamKey = `Double-${digits.join("")}`;
    } else {
      teamKey = `Triple-${digits.join("")}`;
    }
    const odds = lotteryPayoutOdds(mode);
    const next = {
      amount: 0,
      catmid: payload?.cat_mid,
      round: payload?.cat_mid,
      odds,
      lottery_mode: mode,
      teamname: teamKey,
      displayTeamname: teamKey,
      bettype: "b",
      selectRnr: teamKey,
      marketVal: 1,
      game_type: 7,
      market_type: "m",
      game_name: "lottery_virtual"
    };
    setPlaceBetObject(next);
    if (isMobile) {
      setShowBetSlipPopup(true);
    } else {
      setShowBetSlip(true);
    }
  };

  const onLotteryCardClick = (rowIndex, rankIdx) => {
    const px = lotteryRowBackPrice(rowIndex);
    if (isSuspended || payload?.[`suspend${rowIndex + 1}`] == 1 || !(px > 0)) {
      return;
    }

    let rowsNeeded = 3;
    if (lotteryBetMode === "single") {
      rowsNeeded = 1;
    } else if (lotteryBetMode === "double") {
      rowsNeeded = 2;
    }

    const nextSel = { ...selectedCardByRow, [rowIndex]: rankIdx };
    setSelectedCardByRow(nextSel);

    const rankIndexesComplete = [];
    for (let i = 0; i < rowsNeeded; i++) {
      const v = nextSel[i];
      if (v === undefined || v === null) {
        return;
      }
      rankIndexesComplete.push(v);
    }

    openLotteryBetSlip(lotteryBetMode, rankIndexesComplete);
  };

  const placeBet = async (amount) => {
    placeBetObject.amount = amount;

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino",
        placeBetObject
      );
      let messageObj = {};
      if (response?.data?.status) {
        messageObj.status = true;
        setShowBetSlipPopup(false);
        await getPlacedBets();
        await getBook();
      } else {
        messageObj.status = false;
      }
      messageObj.message = response?.data?.message;
      setBetErrorMessage(messageObj);
    } catch (error) {
      const msg = error.response?.data?.message || "Bet failed.";
      let messageObj = { status: false, message: msg };
      if (error.response?.data?.status === false) {
        messageObj.message = error.response.data.message;
      }
      setBetErrorMessage(messageObj);
    }
  };

  const closeBetSlipMobile = () => {
    setShowBetSlipPopup(false);
    setBetErrorMessage({});
  };

  const closeBetSlip = () => {
    setShowBetSlip(false);
  };

  const R = 45;
  const CIRC = 2 * Math.PI * R;
  const ringDenom = Math.max(TOTAL_SECONDS, timerTotal);
  const dash = Math.max(0.0001, CIRC * (timeLeft / ringDenom));
  let timerColor = "green";
  if (timeLeft <= 5) {
    timerColor = "red";
  } else if (timeLeft <= 10) {
    timerColor = "orange";
  }

  const activeTab = (tab1) => {
    setActiveTabIndex(tab1);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [lotteryBetMode, setLotteryBetMode] = useState("single");
  /** rowIndex → rank index 0–9 (A–10) after pick */
  const [selectedCardByRow, setSelectedCardByRow] = useState({});

  useEffect(() => {
    setSelectedCardByRow({});
  }, [lotteryBetMode]);

  useEffect(() => {
    setSelectedCardByRow({});
  }, [payload?.cat_mid]);

  let lotteryCardRowLabels = ["1st Card"];
  if (lotteryBetMode === "double") {
    lotteryCardRowLabels = ["1st Card", "2nd Card"];
  } else if (lotteryBetMode === "triple") {
    lotteryCardRowLabels = ["1st Card", "2nd Card", "3rd Card"];
  }

  const lotteryPayout = lotteryPayoutOdds(lotteryBetMode);

  /** Full betting-area lock like roulette pattern. */
  const showLotteryLockOverlay = !!payload && (isSuspended || timeLeft <= 1);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const lotteryTabClass = (isActive) => {
    if (isActive) {
      return "tab active";
    }
    return "tab";
  };

  return (
    <>
      <div className="lottery-page-scope aviator-scope player-scope aviator-bg-black">
        <ClientLayoutWithOutHeader showLoader={false}>
          <div class="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>

                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                      <TpStudioLoader displayLoader={displayLoader} />
                    </div>

                    <div
                      className="right-side-wrapper-row lottery-game-panel"
                      style={{ display: displayLoader ? "none" : "" }}
                    >
                      <div className="row right-side-row lottery-game-row">
                        <div className="col-12 col-md-8">
                          <div className="left-content">
                            <div>
                              <div className="col-12 timerDiv2">
                                <div
                                  className="footer-divbpg border-gradient-footer border-gradient-purple-footer"
                                  style={{ height: "45px" }}
                                >
                                  <div className="col-6 col-md-6 titlebarname  inline-block Gameidcol mt-2">
                                    <p style={{ fontSize: "17px" }}>
                                      &nbsp;Lottery
                                    </p>
                                  </div>
                                  <div
                                    className="col-6 col-md-6 inline-block Gameidcol text-right"
                                    style={{ marginTop: "2px" }}
                                  >
                                    <div
                                      role="group"
                                      aria-label="Second group"
                                      className="btn-group btn-group-sm mr-2"
                                    >
                                      <button
                                        style={{ fontSize: "14px" }}
                                        type="button"
                                        id="Alltab"
                                        className={
                                          activeTabIndex == 1
                                            ? "tablink btn btn-primary bbactive"
                                            : "tablink btn btn-primary"
                                        }
                                        onClick={() => activeTab(1)}
                                      >
                                        Game
                                      </button>
                                      <button
                                        style={{ fontSize: "14px" }}
                                        type="button"
                                        id="RESULTStab"
                                        className={
                                          activeTabIndex == 2
                                            ? "tablink btn btn-primary bbactive"
                                            : "tablink btn btn-primary"
                                        }
                                        onClick={() => activeTab(2)}
                                      >
                                        Results
                                      </button>
                                      <button
                                        style={{ fontSize: "14px" }}
                                        type="button"
                                        className="tablink btn btn-primary"
                                        onClick={() => setShowRules(true)}
                                      >
                                        Rules
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <LotteryRules
                                  show={showRules}
                                  onClose={() => setShowRules(false)}
                                />
                                <div
                                  style={{
                                    display: activeTabIndex == 1 ? "" : "none"
                                  }}
                                >
                                  <div className="lottery-game-stage">
                                    <div className="row mt-2 lottery-timer-row">
                                      <div className="lottery-bettor-col">
                                        <div className="Connectedclients mt-1">
                                          <i
                                            aria-hidden="true"
                                            className="fa fa-user mr-1"
                                            style={{
                                              fontSize: "14px",
                                              color: "white"
                                            }}
                                          />
                                          <b>
                                            <span
                                              id="UsersCount"
                                              className="number"
                                            >
                                              {payload?.users_online ??
                                                payload?.users_count ??
                                                326}
                                            </span>
                                          </b>
                                        </div>
                                      </div>
                                      <div className="lottery-timer-col">
                                        <div className="teenpatti-base-timer">
                                          <svg
                                            viewBox="0 0 100 100"
                                            className="base-timer__svg"
                                          >
                                            <g className="base-timer__circle">
                                              <circle
                                                cx="50"
                                                cy="50"
                                                r="45"
                                                className="base-timer__path-elapsed"
                                              />
                                              <path
                                                strokeDasharray={`${Math.round(
                                                  dash
                                                )} ${Math.round(CIRC)}`}
                                                className={`base-timer__path-remaining ${timerColor}`}
                                                d="
                                                                                                M 50, 50
                                                                                                m -45, 0
                                                                                                a 45,45 0 1,0 90,0
                                                                                                a 45,45 0 1,0 -90,0
                                                                                            "
                                              />
                                            </g>
                                          </svg>
                                          <span className="base-timer__label">
                                            {timeLeft}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="table-player">
                                      <div className="tp20-player">
                                        <span className="name">Dealer</span>
                                        <div className="tp-hand">
                                          <div
                                            className={`tp-card ${shownAc1 ? "flip" : ""
                                              }`}
                                          >
                                            {shownAc1 && (
                                              <img
                                                src={`/assets/galaxy/img/cards3/${shownAc1}.svg`}
                                                draggable={false}
                                                alt=""
                                              />
                                            )}
                                          </div>
                                          <div
                                            className={`tp-card ${shownAc2 ? "flip" : ""
                                              }`}
                                          >
                                            {shownAc2 && (
                                              <img
                                                src={`/assets/galaxy/img/cards3/${shownAc2}.svg`}
                                                draggable={false}
                                                alt=""
                                              />
                                            )}
                                          </div>
                                          <div
                                            className={`tp-card ${shownAc3 ? "flip" : ""
                                              }`}
                                          >
                                            {shownAc3 && (
                                              <img
                                                src={`/assets/galaxy/img/cards3/${shownAc3}.svg`}
                                                draggable={false}
                                                alt=""
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        textAlign: "right",
                                        marginRight: "20px",
                                        marginTop: "-20px"
                                      }}
                                      className="volume-wrapper"
                                    >
                                      <Volume />
                                    </div>
                                  </div>

                                  <div className="cards-layout blackjack">
                                    {/* Controls removed (auto mode) */}

                                    {/* Footer */}
                                    <center>
                                      <div class="footer-divbpg border-gradient-footer border-gradient-purple-footer">
                                        <div class="buttons-div">
                                          {[0, 1, 2, 3, 4, 5].map((i) => {
                                            let chipText = "–";
                                            if (
                                              recentResultChips[i] != null &&
                                              recentResultChips[i] !== ""
                                            ) {
                                              chipText = recentResultChips[i];
                                            }
                                            return (
                                              <button
                                                key={i}
                                                type="button"
                                                class="video-button border-gradient-btn border-gradient-purple-btn"
                                              >
                                                {chipText}
                                              </button>
                                            );
                                          })}
                                        </div>
                                        <div class="round-div">
                                          <div class="label">
                                            Round ID: {roundId || "–"}
                                          </div>
                                        </div>
                                      </div>
                                    </center>

                                    <div
                                      className="Lottery-odds lottery-betting-wrap"
                                      style={{ position: "relative" }}
                                    >
                                      <div className="tabs-container">
                                        <div className="tabs-footer">
                                          <div className="tabs">
                                            <button
                                              type="button"
                                              className={lotteryTabClass(
                                                lotteryBetMode === "single"
                                              )}
                                              onClick={() =>
                                                setLotteryBetMode("single")
                                              }
                                            >
                                              Single
                                            </button>
                                            <button
                                              type="button"
                                              className={lotteryTabClass(
                                                lotteryBetMode === "double"
                                              )}
                                              onClick={() =>
                                                setLotteryBetMode("double")
                                              }
                                            >
                                              Double
                                            </button>
                                            <button
                                              type="button"
                                              className={lotteryTabClass(
                                                lotteryBetMode === "triple"
                                              )}
                                              onClick={() =>
                                                setLotteryBetMode("triple")
                                              }
                                            >
                                              Triple
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div class="lot-odd-container">
                                        <div class="oddvalue">
                                          Payout = {lotteryPayout}
                                        </div>
                                        {lotteryCardRowLabels.map(
                                          (rowLabel, rowIndex) => {
                                            let rowDomId = `card${rowIndex + 1
                                              }`;
                                            if (rowIndex === 0) {
                                              rowDomId = "card1";
                                            }
                                            return (
                                              <div
                                                key={`lottery-card-row-${rowIndex}`}
                                                id={rowDomId}
                                                class="market-odds-container"
                                                style={{ position: "relative" }}
                                              >
                                                <div class="market-contaner">
                                                  <div class="lot-market-name">
                                                    <span class="lot-market-rank">
                                                      {rowLabel.split(" ")[0]}
                                                    </span>
                                                    <span class="lot-market-word">
                                                      Card
                                                    </span>
                                                  </div>
                                                </div>
                                                <div className="lottery-card-hand">
                                                  <div className="lottery-card-scroll">
                                                    {LOTTERY_PICK_RANKS.map(
                                                      (rankLabel, rankIdx) => {
                                                        const isSelected =
                                                          selectedCardByRow[
                                                          rowIndex
                                                          ] === rankIdx;
                                                        let pickCardClass =
                                                          "lottery-pick-card";
                                                        if (isSelected) {
                                                          pickCardClass +=
                                                            " lottery-card-selected";
                                                        }
                                                        const rowCanBet =
                                                          !isSuspended &&
                                                          payload?.[
                                                          `suspend${rowIndex + 1
                                                          }`
                                                          ] != 1 &&
                                                          lotteryRowBackPrice(
                                                            rowIndex
                                                          ) > 0;
                                                        let cursorStyle =
                                                          "not-allowed";
                                                        let opacityStyle = 0.45;
                                                        if (rowCanBet) {
                                                          cursorStyle =
                                                            "pointer";
                                                          opacityStyle = 1;
                                                        }
                                                        return (
                                                          <div
                                                            className="lottery-card-slot"
                                                            key={`r${rowIndex}-${rankLabel}`}
                                                          >
                                                            <img
                                                              role="presentation"
                                                              className={
                                                                pickCardClass
                                                              }
                                                              src={lotteryPickCardSrc(
                                                                rankLabel
                                                              )}
                                                              draggable={false}
                                                              alt=""
                                                              style={{
                                                                cursor:
                                                                  cursorStyle,
                                                                opacity:
                                                                  opacityStyle
                                                              }}
                                                              onClick={() => {
                                                                onLotteryCardClick(
                                                                  rowIndex,
                                                                  rankIdx
                                                                );
                                                              }}
                                                            />
                                                          </div>
                                                        );
                                                      }
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                      {showLotteryLockOverlay && (
                                        <div
                                          className="overlayRoulettelock lottery-lock-overlay"
                                          aria-hidden="true"
                                        >
                                          <div style={{ textAlign: "center" }}>
                                            <svg
                                              focusable="false"
                                              aria-hidden="true"
                                              viewBox="0 0 24 24"
                                              data-testid="LockIcon"
                                              className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium lock-icon css-vubbuv"
                                              style={{
                                                width: "70px",
                                                height: "70px",
                                                filter: "invert(0)"
                                              }}
                                            >
                                              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                                            </svg>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ height: "5px" }}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            id="ResultsData"
                            className="tabcontent mobile-margin"
                            style={{
                              display: activeTabIndex === 2 ? "block" : "none",
                              marginTop: "25px",
                              marginLeft: "15px"
                            }}
                          >
                            <LotteryResult resultData={resultData} />
                          </div>
                        </div>

                        <div className="col-12 col-md-4 mobile-margin">
                          {showBetSlipPopup && (
                            <TPPopUpBetSlip
                              closeBetSlipParent={closeBetSlipMobile}
                              betObj={placeBetObject}
                              placeBetParent={placeBet}
                              betButtonShow={betButtonShow}
                              betErrorMessageObj={betErrorMessageObj}
                              betButtonShowLoaderShow={betButtonShowLoaderShow}
                              profileData={profileData}
                            />
                          )}

                          <TeenPattisCasinoBetslipWeb
                            showBetSlip={showBetSlip && !isMobile}
                            placeBetObject={placeBetObject}
                            closeBetSlipParent={closeBetSlip}
                            placeBetParent={placeBet}
                            betErrorMessageObj={betErrorMessageObj}
                            profileData={profileData}
                          />
                          <div>
                            <div className="bets">
                              <strong style={{ fontSize: "16px" }}>
                                Matched Bets ({placedBets.length})
                                <img
                                  src={reconnecting}
                                  alt="dc"
                                  className="rounded disconnected"
                                  style={{ display: "none" }}
                                />{" "}
                              </strong>
                              <div className="betting-table">
                                <table className="table">
                                  <thead>
                                    <tr style={{ fontSize: "16px" }}>
                                      <th>Runner</th>
                                      <th>Price</th>
                                      <th>Size</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {placedBets.map((bet, index) => (
                                      <tr key={index}>
                                        <td>
                                          {displayLotteryRunner(bet.rnr)} - ({bet.cat_mid})
                                        </td>
                                        <td>{bet.rate}</td>
                                        <td>{bet.amnt}</td>
                                        <td></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
      <style>
        {` 
                    .lottery-page-scope .home-content {
                        margin-top: 0 !important;
                    }
                    .lottery-page-scope .home-section {
                        height: auto !important;
                        min-height: 100vh;
                    }
                    .lottery-page-scope .details-tabs-wrapper1,
                    .lottery-page-scope .main-page,
                    .lottery-page-scope .bodyheight {
                        height: auto !important;
                        overflow: visible;
                        float: none !important;
                    }
                    .lottery-page-scope .right-side-wrapper-row {
                        height: auto !important;
                        min-height: fit-content;
                        overflow: visible;
                        float: none !important;
                        display: flow-root;
                    }
                    .lottery-page-scope .lottery-game-panel {
                        width: 100%;
                        min-height: fit-content;
                        display: flow-root;
                    }
                    .lottery-page-scope .lottery-game-row {
                        display: flex;
                        flex-wrap: wrap;
                        align-items: stretch;
                        align-content: flex-start;
                        margin: 0;
                        height: auto !important;
                        min-height: fit-content;
                        overflow: visible;
                    }
                    .lottery-page-scope .lottery-game-row > .col-md-8,
                    .lottery-page-scope .lottery-game-row > .col-md-4 {
                        display: flex;
                        flex-direction: column;
                        height: auto;
                        align-self: stretch;
                        overflow: visible;
                        flex: 0 0 100%;
                        max-width: 100%;
                        width: 100%;
                    }
                   
    .lottery-page-scope .col-12,
    .lottery-page-scope .col-md-8 {
        padding-left: 1px !important;
        padding-right: 1px !important;
    }

                    @media (min-width: 768px) {
                        .lottery-page-scope .lottery-game-row > .col-md-8 {
                            flex: 0 0 66.666667%;
                            max-width: 66.666667%;
                        }
                        .lottery-page-scope .lottery-game-row > .col-md-4 {
                            flex: 0 0 33.333333%;
                            max-width: 33.333333%;
                        }
                    }
                    .lottery-page-scope .lottery-game-row > .col-md-8 > .left-content {
                        flex: 1 1 auto;
                        height: auto !important;
                        overflow: visible;
                    }
                    .lottery-page-scope .lottery-game-row > .col-md-4 {
                        padding-bottom: 40px;
                        box-sizing: border-box;
                    }
                    .lottery-page-scope .lottery-game-row > .col-md-4 > div {
                        flex: 1 1 auto;
                        display: flex;
                        flex-direction: column;
                    }
                    .lottery-page-scope .lottery-game-row > .col-md-4 .bets {
                        flex: 1 1 auto;
                    }
                    .lottery-page-scope .lottery-game-stage {
                        position: relative;
                        min-height: 210px;
                        overflow: visible;
                        background: #000000;
                    }
                    .lottery-page-scope .lottery-game-stage::before {
                        content: "";
                        position: absolute;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        left: 0;
                        background: radial-gradient(
                            ellipse 100% 44% at 50% calc(30% + 20px),
                            rgba(62, 193, 65, 0.95) 0%,
                            rgba(62, 193, 65, 0.8) 15%,
                            rgba(30, 80, 35, 0.5) 40%,
                            #1e1e1e 55%,
                            #1e1e1e 100%
                        );
                        opacity: 0.4;
                        pointer-events: none;
                        z-index: 0;
                    }
                    .lottery-page-scope .lottery-game-stage > * {
                        position: relative;
                        z-index: 1;
                    }
                    .lottery-page-scope .lottery-timer-row {
                        display: flex;
                        flex-wrap: nowrap;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        margin-left: 0;
                        margin-right: 0;
                        padding: 0 8px;
                    }
                    .lottery-page-scope .lottery-bettor-col,
                    .lottery-page-scope .lottery-timer-col {
                        flex: 0 0 auto;
                        width: auto;
                        max-width: none;
                        padding: 0;
                    }
                    .lottery-page-scope .lottery-bettor-col {
                        position: relative;
                        top: -25px;
                        left: 2px;
                    }
                    .lottery-page-scope .lottery-timer-col {
                        position: relative;
                        top: -4px;
                        left: 8px;
                    }
                    .lottery-page-scope .cards-layout.blackjack {
                        position: relative;
                        height: auto !important;
                        min-height: 0;
                        overflow: visible;
                        padding-bottom: 0;
                        background: none !important;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lot-odd-container,
                    .lottery-page-scope .lottery-betting-wrap .market-odds-container {
                        min-height: 0;
                        height: auto;
                    }
                    .lottery-page-scope .lottery-betting-wrap .market-odds-container {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        background: linear-gradient(180deg, #464646, #0f0f0f);
                        border-radius: 3px;
                        padding: 10px;
                        width: 100%;
                        box-sizing: border-box;
                    }
                    .lottery-page-scope .lottery-betting-wrap .market-contaner {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: flex-start;
                        width: 20%;
                        text-align: left;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lot-market-name {
                        display: flex;
                        flex-direction: column;
                        text-align: left;
                        align-self: flex-start;
                        color: #fff;
                        font-weight: 700;
                        line-height: 1.15;
                    }
                    .lottery-page-scope .lottery-game-stage .table-player {
                        position: relative;
                        top: -30px;
                        gap: 10px !important;
                        justify-content: center;
                        align-items: flex-start;
                    }
                    .lottery-page-scope .lottery-game-stage .table-player .tp20-player {
                        width: auto !important;
                    }
                    .lottery-page-scope .lottery-game-stage .table-player .tp20-player .name {
                        display: block;
                        color: #d7bc7b;
                        font-size: 14px;
                        font-weight: 700;
                        padding-bottom: 5px;
                        text-align: center;
                    }
                    .lottery-page-scope .lottery-game-stage .table-player .tp-hand {
                        position: relative;
                        width: 220px !important;
                        max-width: 100%;
                        height: 110px;
                        margin: 0 auto;
                        overflow: visible;
                        box-sizing: border-box;
                        border: 1px solid rgba(178, 220, 175, 0.25);
                        border-radius: 16px;
                        background: transparent;
                    }
                    .lottery-page-scope .lottery-game-stage .table-player .tp-hand .tp-card img {
                        width: 64px;
                        height: auto;
                        display: block;
                        border-radius: 8px;
                    }
                    .lottery-page-scope .lottery-game-stage .volume-wrapper {
                        margin-top: -35px;
                    }
                    .lottery-page-scope .lottery-game-stage .volume-wrapper .sound-icon {
                        transform: scale(0.9);
                        transform-origin: center;
                    }
                    @media (min-width: 769px) {
                        .lottery-page-scope .lottery-game-stage .volume-wrapper {
                            position: relative;
                            right: -10px;
                            margin-right: 0 !important;
                        }
                    }
                    .lottery-page-scope .tabs-container {
                        align-items: center;
                        display: flex;
                        justify-content: space-around;
                        pointer-events: visible;
                        margin-top: 10px;
                    }
                    .lottery-page-scope .tabs-container .tabs-footer {
                        background-clip: padding-box, border-box;
                        background-image: linear-gradient(180deg, #464646, #0f0f0f), linear-gradient(180deg, rgba(207, 141, 46, .47), #cf8d2e, rgba(207, 141, 46, .47));
                        background-origin: border-box;
                        border: 2px double transparent;
                        border-radius: 100px;
                        height: 40px;
                        min-height: 35px;
                        width: 60%;
                    }
                    .lottery-page-scope .tabs {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .lottery-page-scope .tab {
                        width: 100px;
                        color: #d1d1d1;
                        font-weight: 600;
                        padding: 3px 0;
                        cursor: pointer;
                        color: #000;
                        border-radius: 50px;
                        transition: all 0.3s ease;
                    }
                    .lottery-page-scope .tab.active {
                        background: linear-gradient(180deg, #e0a13a, #c37b1f);
                        color: black;
                        box-shadow: inset 0 2px 4px rgba(0,0,0,0.3);
                    }
                    .lottery-page-scope .tab:hover:not(.active) {
                        color: white;
                    }
                    .lottery-page-scope .tab:focus,
                    .lottery-page-scope .tab:focus-visible,
                    .lottery-page-scope .tab:active {
                        outline: none !important;
                        box-shadow: none;
                    }
                    .lottery-page-scope .tab.active:focus,
                    .lottery-page-scope .tab.active:focus-visible {
                        box-shadow: inset 0 2px 4px rgba(0,0,0,0.3);
                    }
                    .lottery-page-scope .lottery-betting-wrap .lottery-card-hand {
                        width: 100%;
                        height: auto;
                        min-height: 0;
                        text-align: center;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll {
                        display: flex;
                        justify-content: space-evenly;
                        align-items: flex-start;
                        width: 100%;
                        flex-wrap: nowrap;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lottery-card-slot {
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        flex: 0 0 auto;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lottery-pick-card {
                        width: 37px;
                        height: auto;
                        display: block;
                        border-radius: 5px;
                        flex-shrink: 0;
                    }
                    /* Selection: inset ring + soft glow — no border offset vs neighbors */
                    .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll [role="presentation"] {
                        border: none !important;
                        border-radius: 5px;
                        box-sizing: border-box;
                        transition: box-shadow 0.15s ease;
                    }
                    .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll [role="presentation"].lottery-card-selected {
                        box-shadow:
                            inset 0 0 0 2px rgba(224, 161, 58, 0.95),
                            0 0 10px rgba(224, 161, 58, 0.35);
                        position: relative;
                        z-index: 2;
                    }
                    .lottery-page-scope .lottery-betting-wrap .overlayRoulettelock.lottery-lock-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: rgba(0, 0, 0, 0.8);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        z-index: 12;
                    }
                    .lottery-page-scope .lottery-betting-wrap .overlayRoulettelock.lottery-lock-overlay svg {
                        fill: #fff;
                    }
                    @media (max-width: 767.98px) {
                        .lottery-page-scope .lottery-game-row {
                            flex-direction: column;
                        }
                        .lottery-page-scope .mobile-margin {
                            margin-top: 8px;
                        }
                    }
                    @media (max-width: 768px) {
                        .lottery-page-scope .lottery-game-stage {
                            min-height: 175px;
                        }
                        .lottery-page-scope .tabs-container .tabs-footer {
                            width: 100%;
                        }
                        .lottery-page-scope .lottery-game-stage .table-player .tp-hand {
                            width: min(180px, calc(50vw - 28px)) !important;
                            height: 110px;
                        }
                        .lottery-page-scope .lottery-game-stage .volume-wrapper {
                            position: relative;
                            top: -35px;
                            right: -10px;
                            margin-top: 0 !important;
                            margin-right: 0 !important;
                        }
                        /* Mobile card rows: label left, cards right in dark gray bars */
                        .lottery-page-scope .lottery-betting-wrap .lot-odd-container {
                            gap: 6px;
                            padding: 0 2px;
                        }
                        .lottery-page-scope .lottery-betting-wrap .oddvalue {
                            padding: 6px 0 8px;
                            font-size: 16px;
                            font-weight: 700;
                            color: #cf8d2e;
                            text-align: center;
                        }
                        .lottery-page-scope .lottery-betting-wrap .market-odds-container {
                            flex-direction: row !important;
                            align-items: center !important;
                            background: linear-gradient(180deg, #464646, #0f0f0f) !important;
                            border-radius: 3px;
                            padding: 8px 6px !important;
                            gap: 4px;
                            width: 100%;
                            box-sizing: border-box;
                            border-bottom: 1px solid rgba(0, 0, 0, 0.35);
                        }
                        .lottery-page-scope .lottery-betting-wrap .market-contaner {
                            width: 38px !important;
                            min-width: 38px;
                            max-width: 38px;
                            flex-shrink: 0;
                            justify-content: center !important;
                            align-items: flex-start !important;
                        }
                        .lottery-page-scope .lottery-betting-wrap .lot-market-name {
                            display: flex;
                            flex-direction: column;
                            font-size: 13px;
                            font-weight: 700;
                            color: #fff;
                            line-height: 1.15;
                            text-align: left;
                            white-space: nowrap;
                        }
                        .lottery-page-scope .lottery-betting-wrap .lottery-card-hand {
                            flex: 1 1 auto;
                            width: auto !important;
                            min-width: 0;
                            text-align: left !important;
                            transform: none !important;
                        }
                        .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll {
                            display: flex !important;
                            flex-wrap: nowrap;
                            justify-content: space-between;
                            align-items: center;
                            gap: 2px;
                            overflow-x: visible !important;
                            overflow-y: visible;
                            width: 100%;
                            box-sizing: border-box;
                            padding: 2px 0;
                        }
                        .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll .lottery-card-slot {
                            display: flex !important;
                            flex: 1 1 0;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            width: auto !important;
                            min-width: 0;
                            margin: 0;
                            padding: 0;
                        }
                        .lottery-page-scope .lottery-betting-wrap .lottery-card-scroll .lottery-card-slot .lottery-pick-card {
                            display: block !important;
                            box-sizing: border-box !important;
                            width: 100% !important;
                            max-width: 30px;
                            height: auto !important;
                            margin: 0 auto !important;
                            padding: 0 !important;
                            flex-shrink: 0;
                            transform: none !important;
                        }
                    }
                    
                    .lottery-page-scope .mobile-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: rgba(0,0,0,0.5);
                        z-index: 9999;
                    }
                    .lottery-page-scope .mobile-modal {
                        position: fixed;
                        top: 10px;
                        left: 50%;
                        transform: translateX(-50%);
                        width: 95%;
                        max-width: 420px;
                        background: #dfe7ee; 
                        border-radius: 6px;
                        padding: 15px;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.3);
                        animation: lottery-page-slide-down 0.25s ease;
                    }
                    @keyframes lottery-page-slide-down {
                        from {
                            transform: translate(-50%, -30%);
                            opacity: 0;
                        }
                        to {
                            transform: translate(-50%, 0);
                            opacity: 1;
                        }
                    }
                    @media(max-width:576px){
                      .mobile-margin{
                        margin-top: 2px !important;
                        margin-left: 2px !important;
                      }
                    }
                `}
      </style>
    </>
  );
}
